import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DoorOpen } from "lucide-react";

import { Badge, Button, EmptyState, H3, Mono, Stat, Text } from "@lagoon/ui";

import { api, ApiError, type AccessEvent } from "../api.js";
import { Failure, LoadingPanel, Notice } from "./feedback.js";

/**
 * Who has actually been reading this site.
 *
 * The allowlist says who *may* read it. Until now nothing said who did, which
 * for confidential material is half the reason to gate a site at all.
 *
 * A log of sign-ins, not page views. One row is one session, however many
 * pages followed it. That is the granularity the owner's question needs, and
 * the only one that costs nothing on the request path.
 */
export function Activity(props: { siteId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const log = useQuery({
    queryKey: ["access", props.siteId],
    queryFn: () => api.getAccessLog(props.siteId),
  });

  /**
   * Ending a session, which has to say when it did not end (LGN-128).
   *
   * The row kept its "signed in" badge and the button came back enabled
   * whether the request succeeded or was refused, so a lapsed session or a
   * suspended organisation looked exactly like a sign-out. The notice sits
   * directly above the list, next to the button that was pressed.
   */
  const end = useMutation({
    mutationFn: (sessionId: string) => api.endSession(props.siteId, sessionId),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["access", props.siteId] });
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not end that session. They are still signed in.",
      );
    },
  });

  if (log.isPending) {
    return <LoadingPanel label="Loading the activity" />;
  }
  if (log.isError) {
    return <Failure error={log.error} fallback="Could not load the activity." />;
  }

  const { summary, events } = log.data;

  return (
    <section className="lg:flex lg:flex-col lg:gap-5">
      <div>
        <H3>Who has been in</H3>
        <Text size="sm" tone="muted" className="lg:mt-1">
          One row per sign-in, newest first. Not page views: someone who signs in once and reads
          twenty pages appears once.
        </Text>
      </div>

      {events.length === 0 ? (
        <EmptyState
          icon={<DoorOpen />}
          title="Nobody has opened this site yet"
          description="When someone signs in with a passcode or a guest link, they will appear here."
        />
      ) : (
        <>
          <div className="lg:grid lg:grid-cols-2 lg:gap-4 lg:md:grid-cols-4">
            <Stat value={String(summary.distinctVisitors)} label="People" />
            <Stat value={String(summary.totalSessions)} label="Sign-ins" />
            {/*
              Amber for the same reason the badge is: nothing is denying these
              people access, their mail is simply not arriving. `Stat` carries
              no tone of its own, so the warning colour is applied to its value
              slot; it is still the `--warning` token and not a literal.
            */}
            <Stat
              value={String(summary.bouncingOnPage)}
              label="Bouncing"
              className={warnWhen(summary.bouncingOnPage > 0)}
            />
            {/* The number worth acting on: somebody kept knocking after they
                lost access. */}
            <Stat
              value={String(summary.lockedOut)}
              label="Turned away"
              className={warnWhen(summary.lockedOut > 0)}
            />
          </div>

          {error === null ? null : <Notice tone="danger">{error}</Notice>}

          <ul className="lg:flex lg:flex-col lg:gap-px lg:overflow-hidden lg:rounded-lg lg:border lg:border-hairline lg:bg-hairline">
            {events.map((event) => (
              <AccessRow
                key={event.sessionId}
                event={event}
                onEnd={() => {
                  end.mutate(event.sessionId);
                }}
                ending={end.isPending}
              />
            ))}
          </ul>

          {summary.truncated ? (
            <Text size="caption" tone="subtle">
              Showing the most recent {events.length}. Older sign-ins are kept for 90 days.
            </Text>
          ) : null}
        </>
      )}
    </section>
  );
}

/** Amber on the stat's value when the number is one worth looking at. */
function warnWhen(condition: boolean): string | undefined {
  return condition ? "lg:[&_[data-slot=stat-value]]:text-warning" : undefined;
}

function AccessRow(props: {
  event: AccessEvent;
  onEnd: () => void;
  ending: boolean;
}): React.ReactElement {
  const { event } = props;

  return (
    <li className="lg:flex lg:flex-wrap lg:items-start lg:gap-4 lg:bg-card lg:px-5 lg:py-4">
      <div className="lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-1.5">
        <span className="lg:flex lg:flex-wrap lg:items-center lg:gap-2">
          <span className="lg:font-medium lg:text-strong">{describeWho(event)}</span>
          <Badge tone={event.active ? "success" : "neutral"}>
            {event.active ? "signed in" : event.revokedAt ? "access ended" : "session over"}
          </Badge>
          {event.deniedCount > 0 ? (
            <Badge tone="danger" title={`Last tried ${formatWhen(event.lastDeniedAt)}`}>
              tried again {event.deniedCount}×
            </Badge>
          ) : null}
          {event.bouncing ? (
            <Badge tone="warning" title={describeBounce(event.bounceReason)}>
              email bouncing
            </Badge>
          ) : null}
        </span>

        <Text as="span" size="caption" tone="muted">
          {describeHow(event)} · started {formatWhen(event.startedAt)} · last seen{" "}
          {formatWhen(event.lastSeenAt)}
        </Text>

        {event.ip === null && event.userAgent === null ? null : (
          <Mono className="lg:text-micro lg:text-subtle">
            {[event.ip, shortenAgent(event.userAgent)].filter(Boolean).join(" · ")}
          </Mono>
        )}

        {event.revokedReason === null ? null : (
          <Text as="span" size="caption" tone="subtle">
            Ended: {event.revokedReason}
          </Text>
        )}
      </div>

      {event.active ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={props.ending}
          onClick={props.onEnd}
          title="Sign this one session out. Does not remove them from the list."
        >
          Sign out
        </Button>
      ) : null}
    </li>
  );
}

/**
 * A guest has no address, only the label of the pass they used, so the two
 * cases genuinely read differently and should not be forced into one shape.
 */
function describeWho(event: AccessEvent): string {
  if (event.email !== null) return event.email;
  return event.guestLabel === null ? "Guest link" : `Guest: ${event.guestLabel}`;
}

/**
 * What the provider actually said, in the owner's language.
 *
 * The raw event name is SendGrid's vocabulary and means nothing to somebody
 * who just wants to know why their colleague cannot sign in.
 */
export function describeBounce(reason: string | null): string {
  if (reason === "spamreport") {
    return "They marked a previous message as spam, so we have stopped sending to this address.";
  }
  if (reason === "dropped") return "The provider refused to send to this address.";
  if (reason === "manual") return "Sending to this address was stopped by hand.";
  return "Mail to this address bounced, so passcodes are not being sent to it.";
}

function describeHow(event: AccessEvent): string {
  if (event.method === "owner") return "you, as the owner";
  if (event.method === "guest_pass") return "guest link";
  return "emailed passcode";
}

/** Browsers send a paragraph; the useful part is the first token. */
function shortenAgent(agent: string | null): string | null {
  if (agent === null) return null;
  const match = /(Firefox|Edg|Chrome|Safari)\/[\d.]+/.exec(agent);
  if (match) return match[0].replace("Edg", "Edge");
  return agent.slice(0, 40);
}

function formatWhen(iso: string | null): string {
  if (iso === null) return "never";
  const date = new Date(iso);
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
